/**
 * AWS Signature Version 4 — minimal, dependency-free signer for S3-compatible
 * endpoints, implemented with the Web Crypto API. No AWS SDK.
 *
 * Only `host`, `x-amz-date`, and `x-amz-content-sha256` are included in the
 * signed-headers set — that is the minimum S3 requires. Any other request
 * headers (e.g. Content-Type) are sent unsigned, which S3 permits.
 *
 * Ported verbatim from yaqatw's src/service/cloud/sigv4.ts.
 *
 * Reference:
 * https://docs.aws.amazon.com/general/latest/gr/sigv4-create-canonical-request.html
 */

export interface SignRequestParams {
  method: string;
  /** Full request URL including any query string. */
  url: string;
  region: string;
  service: string; // "s3"
  accessKeyId: string;
  secretAccessKey: string;
  /** Request body bytes. Empty/omitted hashes the empty string. */
  body?: Uint8Array | string;
  /** Override the request timestamp. Defaults to now; used by tests. */
  date?: Date;
}

const encoder = new TextEncoder();

function toBytes(data: Uint8Array | string | undefined): Uint8Array {
  if (data === undefined) return new Uint8Array(0);
  return typeof data === "string" ? encoder.encode(data) : data;
}

function toHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let out = "";
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, "0");
  }
  return out;
}

async function sha256Hex(data: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", data as BufferSource);
  return toHex(digest);
}

async function hmac(key: Uint8Array, data: string): Promise<ArrayBuffer> {
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key as BufferSource,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return crypto.subtle.sign("HMAC", cryptoKey, encoder.encode(data) as BufferSource);
}

/**
 * RFC 3986 encoding. AWS SigV4 encodes every byte except A-Z a-z 0-9 - _ . ~.
 * For S3 path segments the slash is preserved by the caller (we encode per
 * segment), so this never sees a separator it must keep.
 */
function rfc3986(str: string): string {
  return encodeURIComponent(str).replace(
    /[!'()*]/g,
    (c) => "%" + c.charCodeAt(0).toString(16).toUpperCase()
  );
}

function canonicalUri(pathname: string): string {
  return pathname
    .split("/")
    .map((segment) => (segment ? rfc3986(decodeURIComponent(segment)) : ""))
    .join("/");
}

function canonicalQuery(searchParams: URLSearchParams): string {
  const pairs: [string, string][] = [];
  searchParams.forEach((value, key) => pairs.push([key, value]));
  pairs.sort((a, b) => (a[0] < b[0] ? -1 : a[0] > b[0] ? 1 : a[1] < b[1] ? -1 : 1));
  return pairs.map(([k, v]) => `${rfc3986(k)}=${rfc3986(v)}`).join("&");
}

/** YYYYMMDD'T'HHMMSS'Z' (the basic-format ISO-8601 SigV4 wants). */
function amzDate(now: Date): string {
  return now.toISOString().replace(/[:-]|\.\d{3}/g, "");
}

/**
 * Returns the headers a signed request must carry. Merge these into the fetch
 * `headers`; do NOT set `Host` yourself (the browser owns it) — it is only used
 * here for the signature.
 */
export async function signRequest(
  params: SignRequestParams
): Promise<{ Authorization: string; "x-amz-date": string; "x-amz-content-sha256": string }> {
  const { method, region, service, accessKeyId, secretAccessKey } = params;
  const url = new URL(params.url);

  const now = params.date ?? new Date();
  const amzdate = amzDate(now);
  const dateStamp = amzdate.slice(0, 8);

  const payloadHash = await sha256Hex(toBytes(params.body));

  // Canonical headers — signed set is host;x-amz-content-sha256;x-amz-date.
  const signedHeaders = "host;x-amz-content-sha256;x-amz-date";
  const canonicalHeaders =
    `host:${url.host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzdate}\n`;

  const canonicalRequest = [
    method.toUpperCase(),
    canonicalUri(url.pathname),
    canonicalQuery(url.searchParams),
    canonicalHeaders,
    signedHeaders,
    payloadHash,
  ].join("\n");

  const scope = `${dateStamp}/${region}/${service}/aws4_request`;
  const stringToSign = [
    "AWS4-HMAC-SHA256",
    amzdate,
    scope,
    await sha256Hex(encoder.encode(canonicalRequest)),
  ].join("\n");

  // Derive the signing key.
  const kDate = await hmac(encoder.encode(`AWS4${secretAccessKey}`), dateStamp);
  const kRegion = await hmac(new Uint8Array(kDate), region);
  const kService = await hmac(new Uint8Array(kRegion), service);
  const kSigning = await hmac(new Uint8Array(kService), "aws4_request");
  const signature = toHex(await hmac(new Uint8Array(kSigning), stringToSign));

  const authorization =
    `AWS4-HMAC-SHA256 Credential=${accessKeyId}/${scope}, ` +
    `SignedHeaders=${signedHeaders}, Signature=${signature}`;

  return {
    Authorization: authorization,
    "x-amz-date": amzdate,
    "x-amz-content-sha256": payloadHash,
  };
}
